import axios from '../utils/axios';

class TemplateService {
  /**
   * Get a single template by ID
   */
  async getTemplate(id) {
    try {
      const response = await axios.get(`/templates/${id}`);
      return response.data.data;
    } catch (error) {
      console.error('Failed to get template:', error);
      throw error;
    }
  }

  /**
   * Update an existing template
   */
  async updateTemplate(id, data) {
    try {
      const response = await axios.put(`/templates/${id}`, data);
      return response.data.data;
    } catch (error) {
      console.error('Failed to update template:', error);
      throw error;
    }
  }

  /**
   * Get templates with flexible loading options
   */
  async getTemplates(options = {}) {
    const {
      type = 'public',
      page = 1,
      perPage = 15,
      search = '',
      category = null
    } = options;

    try {
      let endpoint = '/templates';
      const params = new URLSearchParams();

      // Set endpoint based on type
      switch (type) {
        case 'public':
          endpoint = '/templates/public/list';
          break;
        case 'my-templates':
          endpoint = '/templates/user/list';
          break;
        case 'defaults':
          endpoint = '/templates/defaults/list';
          break;
        case 'organization':
          endpoint = '/templates/organization/list';
          break;
        default:
          break;
      }

      // Add filters
      params.append('page', page);
      params.append('per_page', perPage);

      if (search) {
        params.append('search', search);
      }

      if (category) {
        params.append('category', category);
      }

      const queryString = params.toString();
      const url = queryString ? `${endpoint}?${queryString}` : endpoint;

      let response = await axios.get(url);

      // Fallback for public templates if empty or missing data
      if (type === 'public' && (!response?.data?.data || (Array.isArray(response.data.data) && response.data.data.length === 0))) {
        const altParams = new URLSearchParams();
        altParams.append('per_page', perPage);
        altParams.append('visibility', 'public');
        if (search) altParams.append('search', search);
        if (category) altParams.append('category', category);
        const altUrl = `/templates?${altParams.toString()}`;
        try {
          const altResp = await axios.get(altUrl);
          if (altResp?.data?.data) {
            response = altResp;
          }
        } catch (e) {
          // ignore, we'll use original response
        }
      }

      // Normalize to array regardless of nesting
      const raw = response?.data?.data;
      const templatesData = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.data)
          ? raw.data
          : Array.isArray(response?.data)
            ? response.data
            : [];

      const meta = response?.data?.meta || raw?.meta || {
        current_page: 1,
        last_page: 1,
        per_page: perPage,
        total: templatesData.length
      };

      return {
        templates: templatesData,
        meta
      };
    } catch (error) {
      console.error('Failed to load templates:', error);

      // Fallback to localStorage or empty array
      try {
        const storedTemplates = localStorage.getItem('templates');
        const parsedTemplates = storedTemplates ? JSON.parse(storedTemplates) : [];

        return {
          templates: parsedTemplates,
          meta: {
            current_page: 1,
            last_page: 1,
            per_page: 15,
            total: parsedTemplates.length
          }
        };
      } catch (fallbackError) {
        console.error('Error loading from localStorage:', fallbackError);
        return {
          templates: [],
          meta: {
            current_page: 1,
            last_page: 1,
            per_page: 15,
            total: 0
          }
        };
      }
    }
  }

  /**
   * Get template statistics
   */
  async getTemplateStatistics() {
    try {
      const response = await axios.get('/templates/meta/statistics');
      return response.data.data || {};
    } catch (error) {
      console.error('Failed to load template statistics:', error);
      return {};
    }
  }

  /**
   * Get template categories
   */
  async getCategories() {
    try {
      const response = await axios.get('/templates/meta/categories');
      return response.data.data || {};
    } catch (error) {
      console.error('Failed to load template categories:', error);
      return {};
    }
  }

  /**
   * Get template visibility options
   */
  async getVisibilityOptions() {
    try {
      const response = await axios.get('/templates/meta/visibility-options');
      return response.data.data || {};
    } catch (error) {
      console.error('Failed to load template visibility options:', error);
      return {};
    }
  }

  /**
   * Create a new template
   * @param {Object} templateData - The template data to create
   * @returns {Promise<Object>} The created template data
   */
  async createTemplate(templateData) {
    try {
      console.log('TemplateService: Sending data:', templateData);
      console.log('TemplateService: template_data exists?', !!templateData.template_data);
      console.log('TemplateService: messages exists?', !!templateData.template_data?.messages);
      
      const response = await axios.post('/templates', templateData);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to create template');
      }
    } catch (error) {
      console.error('Failed to create template:', error);
      console.error('Error response data:', error.response?.data);

      const errorMessage = error.response?.data?.message ||
                           error.message ||
                           'Failed to create template. Please try again.';

      throw new Error(errorMessage);
    }
  }

  /**
   * Clone a template by its ID
   * @param {number} templateId - The ID of the template to clone
   * @returns {Promise<Object>} The cloned template data
   */
  async cloneTemplate(templateId) {
    try {
      const response = await axios.post(`/templates/${templateId}/clone`);

      // Ensure we return the cloned template data
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to clone template');
      }
    } catch (error) {
      console.error('Failed to clone template:', error);

      // Provide a more informative error message
      const errorMessage = error.response?.data?.message ||
                           error.message ||
                           'Failed to clone template. Please try again.';

      throw new Error(errorMessage);
    }
  }

  /**
   * Delete a template by its ID
   * @param {number} templateId - The ID of the template to delete
   * @returns {Promise<Object>} The response from the server
   */
  async deleteTemplate(templateId) {
    try {
      const response = await axios.delete(`/templates/${templateId}`);

      // Ensure we return the response data
      if (response.data.success) {
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to delete template');
      }
    } catch (error) {
      console.error('Failed to delete template:', error);

      // Provide a more informative error message
      const errorMessage = error.response?.data?.message ||
                           error.message ||
                           'Failed to delete template. Please try again.';

      throw new Error(errorMessage);
    }
  }
}

const templateService = new TemplateService();
export default templateService;
